/* EBF sale page: behaviour for the ebf-* sections.
 *
 * The client's ebf-sale.js (2026-09-23 drop), reorganised for a page built from separate
 * sections: every .ebf-lp root is set up on its own (and again when the theme editor reloads
 * it), while the schedule, the sale bar and the cart state are shared page-wide. Behaviour is
 * the tested page's: phase clock and countdown, sale bar show/hide, model chips, rug sizes with
 * stock and sold-out-last sorting, rail arrows, dataLayer events, and the real cart via
 * /cart/*.js with the lp attributes.
 *
 * Changes from the client file:
 *  - many roots instead of #ebf-lp; every section renders the script tag, so it guards itself
 *  - the cart drawer is the theme's Horizon drawer (CartLinesUpdateEvent from @shopify/events),
 *    with window.EBF_OPEN_CART and the old pop-cart drawer kept as fallbacks
 *  - pills stay in step when a line is changed in the drawer
 *  - setPic's width regex (/width=d+/ → /width=\d+/), so swapped photos get the full srcset
 *  - a bad schedule no longer throws and stops the rest of the page
 */
(function(){
  if (window.__ebfSale) return; window.__ebfSale = true;
  window.dataLayer = window.dataLayer || [];

  var still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var design = !!(window.Shopify && window.Shopify.designMode);
  function withCarry(h){ return h; }   // same domain: nothing to carry
  function money(v){ var n = Number(v); return '$' + (n % 1 === 0 ? n : n.toFixed(2)); }
  function bar(){ return document.querySelector('.ebf-lp--bar[data-starts]'); }
  function page(){ var b = bar(); return (b && b.dataset.page) || 'shop-the-sale-embed'; }
  var track = function(n, p){ window.dataLayer.push(Object.assign({event:n, lp_page:page()}, p || {})); };

  /* ---------- phase clock ---------- */
  function salePhase(schedule, now) {
    var t = typeof now === 'number' ? now : Date.now();
    var starts = Date.parse(schedule.starts);
    var ends = Date.parse(schedule.ends);
    var ext = schedule.extensionEnds ? Date.parse(schedule.extensionEnds) : NaN;
    if (!(starts < ends)) throw new Error('sale schedule: starts must be before ends');
    if (!isNaN(ext) && !(ext > ends)) throw new Error('sale schedule: extensionEnds must be after ends');
    if (t < starts) return { phase: 'pre', target: schedule.starts };
    if (t < ends) return { phase: 'sale', target: schedule.ends };
    if (!isNaN(ext) && t < ext) return { phase: 'extension', target: schedule.extensionEnds };
    return { phase: 'ended', target: null };
  }

  /* The review clock, kept on purpose: ?now=<ISO> (or a bare ?now, the day the sale opens)
   * re-runs the real salePhase() against an overridden instant. Nothing changes for a shopper
   * who does not type it. */
  var NOW_TZ = '-05:00';
  var NOW_DEFAULT = '2026-09-23';
  function reviewNow(raw, tz, fallback) {
    var s = String(raw == null ? '' : raw).trim();
    if (!s) s = String(fallback || '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s = s + 'T12:00:00' + tz;
    else if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?$/.test(s)) s = s.replace(' ', 'T') + tz;
    return Date.parse(s);
  }
  var incoming = new URLSearchParams(location.search);
  var nowRaw = incoming.has('now') ? incoming.get('now') : incoming.has('lp_now') ? incoming.get('lp_now') : null;
  var forced = nowRaw === null ? NaN : reviewNow(nowRaw, NOW_TZ, NOW_DEFAULT);
  var skew = isNaN(forced) ? 0 : forced - Date.now();
  var now = skew ? function(){ return Date.now() + skew; } : Date.now;

  var current = null;
  function applyPhase(){
    var B = bar(); if (!B) { current = null; return null; }
    var s;
    try {
      s = salePhase({ starts: B.dataset.starts, ends: B.dataset.ends, extensionEnds: B.dataset.extensionEnds || undefined }, now());
    } catch (e) { current = null; return null; }   // a mistyped date: no countdown, nothing else breaks
    if (s.phase === 'ended') {
      // Never a dead timer. Liquid redirects first; this covers a cached page.
      if (B.dataset.redirect === '1' && !design) location.replace(B.dataset.after || '/');
      current = s; return s;
    }
    if (!current || current.phase !== s.phase || B.dataset.phase !== s.phase) {
      B.dataset.phase = s.phase;
      B.querySelectorAll('[data-phase]').forEach(function(el){ el.hidden = el.getAttribute('data-phase') !== s.phase; });
    }
    current = s;
    return s;
  }

  /* ---------- the sale bar ----------
   * An IntersectionObserver decides, the state is held in a variable, and the class is written
   * only when it changes (writing it every frame made the bar flicker). The bar belongs to the
   * page after the hero; on a page without a hero it shows straight away. */
  var up = false, heroVisible = false, cartN = 0, heroObs = null;
  function paint(){
    var B = bar(), sticky = B && B.querySelector('.ebf-sticky');
    if (!sticky) return;
    var wantUp = (cartN > 0 || !heroVisible) && !!current && current.phase !== 'ended';
    if (wantUp !== up || sticky.classList.contains('ebf-on') !== wantUp) { up = wantUp; sticky.classList.toggle('ebf-on', up); }
  }
  function watchHero(){
    var hero = document.querySelector('.ebf-lp .ebf-hero');
    if (heroObs) { heroObs.disconnect(); heroObs = null; }
    heroVisible = !!hero;
    if (!hero) { paint(); return; }
    if ('IntersectionObserver' in window) {
      heroObs = new IntersectionObserver(function(es){
        es.forEach(function(e){ heroVisible = e.intersectionRatio > 0; });
        paint();
      }, {rootMargin: '-56px 0px 0px 0px'});
      heroObs.observe(hero);
    } else {
      addEventListener('scroll', function(){ heroVisible = hero.getBoundingClientRect().bottom > 56; paint(); }, {passive:true});
    }
  }

  var pad = function(n){ return (n < 10 ? '0' : '') + n; };
  function tick(){
    var s = applyPhase();
    var B = bar(), left = B && B.querySelector('[data-left]');
    if (s && s.phase !== 'ended' && s.target && left) {
      var ms = Math.max(0, Date.parse(s.target) - now());
      var t = Math.floor(ms / 1000), dd = Math.floor(t / 86400), hh = Math.floor(t % 86400 / 3600), mm = Math.floor(t % 3600 / 60), ss = t % 60;
      var txt = (dd ? dd + 'd ' : '') + pad(hh) + ':' + pad(mm) + ':' + pad(ss);
      if (left.textContent !== txt) left.textContent = txt;
    }
    paint();
    setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
  }

  /* ---------- cards ---------- */
  // Swapping a card photo: rebuild the same four-width ladder the markup ships.
  function setPic(pic, url){
    if (!pic || !url) return;
    pic.srcset = [246, 300, 492, 600].map(function(w){
      return url.replace(/width=\d+/, 'width=' + w) + ' ' + w + 'w';
    }).join(', ');
    pic.src = url;
  }

  // Model picker. Everything the card shows for a plank travels on the chip.
  function initPicks(root){
    root.querySelectorAll('.ebf-card--pick').forEach(function(card){
      var chips = [].slice.call(card.querySelectorAll('.ebf-mod'));
      var img = card.querySelector('.ebf-card__pic');
      var priceB = card.querySelector('.ebf-card__price b');
      var priceS = card.querySelector('.ebf-card__price s');
      var spec = card.querySelector('[data-spec]');
      var add = card.querySelector('.ebf-qa');
      var metas = [].slice.call(card.querySelectorAll('a[data-cta$="-pdp"], .ebf-card__meta'));
      chips.forEach(function(chip){
        chip.addEventListener('click', function(){
          chips.forEach(function(c){ c.setAttribute('aria-pressed', String(c === chip)); });
          var d = chip.dataset;
          if (img) setPic(img, d.img);
          if (spec) spec.textContent = d.spec || '';
          if (priceB) priceB.textContent = money(d.price);
          if (priceS) { priceS.textContent = d.was ? money(d.was) : ''; priceS.hidden = !d.was; }
          if (add) { add.href = withCarry(d.href); add.dataset.name = (add.dataset.name || '').split(' · ')[0] + ' · ' + d.model; }
          metas.forEach(function(a){ a.href = withCarry(d.pdp); });
          track('lp_model_pick', {cta: card.querySelector('.ebf-card__name').textContent.trim(), kit: d.model});
        });
      });
      // A card whose pressed chip was dropped opens on the first one left.
      if (chips.length && !card.querySelector('.ebf-mod[aria-pressed="true"]')) chips[0].click();
    });
  }

  // Rug size control. One group per rail: picking a size repaints every card at once.
  function initSizes(root){
    root.querySelectorAll('.ebf-sizes').forEach(function(group){
      var wrap = group.closest('.ebf-railwrap');
      var cards = [].slice.call(wrap.querySelectorAll('.ebf-card--size'));
      var chips = [].slice.call(group.querySelectorAll('.ebf-mod'));
      var rail = wrap.querySelector('.ebf-rail');
      var soLabel = group.dataset.so || 'Sold out';
      var preLabel = group.dataset.pre || 'Pre order';
      var baseCta = group.dataset.cta || '';
      /* Out-of-stock rugs to the end: in stock, then pre-order, then sold out, each kept in
       * content order (data-ord). It moves the cards themselves, so Tab order follows. */
      var outLast = group.hasAttribute('data-outlast');
      var tail = rail && rail.querySelector('.ebf-card--kit, .ebf-card--all');
      function resort(tiers){
        var nowOrder = [].slice.call(rail.querySelectorAll('.ebf-card--size'));
        var want = cards.map(function(c, i){ return {c: c, t: tiers[i], o: Number(c.dataset.ord) || 0}; })
          .sort(function(a, b){ return a.t - b.t || a.o - b.o; }).map(function(x){ return x.c; });
        if (want.every(function(c, i){ return nowOrder[i] === c; })) return;
        want.forEach(function(c){ rail.insertBefore(c, tail); });
      }
      function pick(size){
        chips.forEach(function(c){ c.setAttribute('aria-pressed', String(c.dataset.size === size)); });
        var tiers = [];
        cards.forEach(function(card, n){
          var list; try { list = JSON.parse(card.dataset.sizes || '[]'); } catch(e){ list = []; }
          var o = list.filter(function(s){ return s.s === size; })[0];
          card.hidden = !o;
          tiers[n] = !o ? 0 : o.so ? 2 : o.pre ? 1 : 0;
          if (!o) return;
          var out = !!o.so, pre = !!o.pre;
          var img = card.querySelector('.ebf-card__pic');
          var priceB = card.querySelector('.ebf-card__price b');
          var priceS = card.querySelector('.ebf-card__price s');
          var add = card.querySelector('.ebf-qa'), addL = card.querySelector('.ebf-qa__l');
          var stock = card.querySelector('[data-stock]');
          card.classList.toggle('ebf-is-out', out);
          if (img) setPic(img, o.i);
          if (priceB) priceB.textContent = money(o.p);
          if (priceS) { priceS.textContent = o.w ? money(o.w) : ''; priceS.hidden = !o.w; }
          if (add) {
            add.classList.toggle('ebf-qa--off', out && !pre);
            add.classList.toggle('ebf-qa--pre', pre);
            if (pre) {
              // A backorder: the pill becomes a plain link to that variant's product page.
              add.href = withCarry(o.u); add.dataset.pre = '1';
              add.classList.remove('ebf-is-added');
              add.removeAttribute('role'); add.removeAttribute('aria-pressed');
              add.removeAttribute('aria-disabled'); add.removeAttribute('title');
              add.setAttribute('aria-label', preLabel + ' ' + (add.dataset.name || '').split(' · ')[0] + ' · ' + o.l);
              add.dataset.cta = baseCta + '-preorder';
            } else if (out || !o.h) {
              add.removeAttribute('href'); add.setAttribute('role', 'link');
              add.setAttribute('aria-disabled', 'true'); add.title = soLabel;
              delete add.dataset.pre; add.dataset.cta = baseCta;
            } else {
              add.href = withCarry(o.h); add.removeAttribute('role');
              add.removeAttribute('aria-disabled'); add.removeAttribute('title');
              delete add.dataset.pre; add.dataset.cta = baseCta;
            }
          }
          if (addL) addL.textContent = pre ? preLabel : out ? soLabel : (addL.dataset.tpl ? addL.dataset.tpl.replace('{size}', o.l) : addL.textContent);
          if (stock) {
            var q = Number(o.q) || 0;
            stock.hidden = !(q > 0);
            if (q > 0) stock.textContent = (stock.dataset.tpl || 'Only {n} left').replace('{n}', String(q));
          }
          [].slice.call(card.querySelectorAll('a[data-cta$="-pdp"], .ebf-card__meta')).forEach(function(a){ a.href = withCarry(o.u); });
        });
        if (outLast && rail) resort(tiers);
        if (rail) rail.scrollTo({left: 0, behavior: still ? 'auto' : 'smooth'});
      }
      chips.forEach(function(chip){
        chip.addEventListener('click', function(){
          if (chip.getAttribute('aria-disabled') === 'true') return;
          if (chip.getAttribute('aria-pressed') === 'true') return;
          pick(chip.dataset.size);
          track('lp_rug_size', {cta: group.dataset.cta || 'rail', kit: chip.dataset.label});
        });
      });
      // Open on the first size someone can buy.
      var first = chips.filter(function(c){ return c.getAttribute('aria-pressed') === 'true'; })[0]
               || chips.filter(function(c){ return c.getAttribute('aria-disabled') !== 'true'; })[0] || chips[0];
      if (first) pick(first.dataset.size);
    });
  }

  // Rails: arrows page by a whole card, and disable at each end.
  function initRails(root){
    root.querySelectorAll('.ebf-railwrap').forEach(function(wrap){
      var rail = wrap.querySelector('.ebf-rail'), nav = wrap.querySelector('.ebf-railnav');
      if (!rail || !nav) return;
      var btns = nav.querySelectorAll('.ebf-arrow');
      function step(){
        var card = rail.querySelector('.ebf-card:not([hidden])');
        return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).gap || 12) : rail.clientWidth * 0.8;
      }
      function sync(){
        var max = rail.scrollWidth - rail.clientWidth - 4;
        btns[0].setAttribute('aria-disabled', rail.scrollLeft <= 4 ? 'true' : 'false');
        btns[1].setAttribute('aria-disabled', rail.scrollLeft >= max ? 'true' : 'false');
      }
      btns.forEach(function(b){
        b.addEventListener('click', function(){
          if (b.getAttribute('aria-disabled') === 'true') return;
          rail.scrollBy({left: step() * Number(b.dataset.dir), behavior: still ? 'auto' : 'smooth'});
          track('lp_rail_arrow', {cta: rail.getAttribute('aria-label') || '', kit: b.dataset.dir === '1' ? 'next' : 'prev'});
        });
      });
      rail.addEventListener('scroll', sync, {passive:true});
      addEventListener('resize', sync);
      sync();
    });
  }

  document.addEventListener('click', function(e){
    var a = e.target.closest && e.target.closest('.ebf-lp a[data-cta]'); if (!a) return;
    if (a.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }   // a sold-out pill is not a CTA
    if (a.dataset.basket) return;   // the cart code reports these
    var B = bar();
    window.dataLayer.push({event:'lp_cta_click', lp_page:page(), cta_location:a.dataset.cta, sale_phase:(B && B.dataset.phase) || ''});
  }, true);

  /* ---------- the real cart ----------
   * A pill adds one of its variant through /cart/add.js, tags the cart with the page
   * (attributes lp / lp_variant / Landing page) and opens the theme's drawer; a second tap on a
   * pill whose variant is in the cart removes that line. The sale bar reads /cart.js and says
   * what is really there. Nothing is stored on the page, so nothing can be re-sent.
   * With JS off every pill is still a plain additive link to /cart. */
  var snap = { count: 0, total: 0, lines: {} };        // the last /cart.js seen, by variant id
  var themeEvents = null, selfEvent = false;
  function allPills(){ return [].slice.call(document.querySelectorAll('.ebf-lp a.ebf-qa')); }
  function cartAttrs(){
    var d = (bar() || {dataset:{}}).dataset;
    return { lp: d.lp || 'shop-the-sale', lp_variant: d.arm || 'onsite', 'Landing page': d.landing || 'lp-shop-the-sale-embed' };
  }
  function idOf(a){ var m = /[?&]id=(\d+)/.exec(a.getAttribute('href') || ''); return m ? m[1] : ''; }
  function cash(n){ return '$' + (Math.round(n * 100) / 100).toFixed(2).replace(/[.]00$/, ''); }
  function noun(n){ return n + ' item' + (n === 1 ? '' : 's'); }
  function nameOf(a){
    var id = idOf(a), card = a.closest('.ebf-card'), name = (a.dataset.name || '').split(' · ')[0];
    if (card) {
      var list = []; try { list = JSON.parse(card.dataset.sizes || '[]'); } catch(e){}
      var size = list.filter(function(s){ return String(s.h || '').indexOf('id=' + id) > -1; })[0];
      if (size) name += ' · ' + size.l;
      var chip = [].slice.call(card.querySelectorAll('.ebf-mod[data-href]')).filter(function(c){ return c.dataset.href.indexOf('id=' + id) > -1; })[0];
      if (chip) name += ' · ' + chip.dataset.model;
    }
    return name;
  }
  function api(path, payload){
    return fetch(path, {method:'POST', headers:{'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify(payload)})
      .then(function(r){ if (!r.ok) throw new Error(path + ' ' + r.status); return r.json(); });
  }
  function absorb(c){
    snap = { count: c.item_count || 0, total: (c.total_price || 0) / 100, lines: {} };
    (c.items || []).forEach(function(it){ snap.lines[String(it.variant_id)] = it; });
    render();
    if (!themeEvents) {
      // Themes without cart events repaint their header count only inside their own drawer refresh.
      [].slice.call(document.querySelectorAll('.js-cart-count')).forEach(function(el){ el.textContent = String(snap.count); });
      [].slice.call(document.querySelectorAll('.cart-count')).forEach(function(el){ el.classList.toggle('cart-empty', !snap.count); });
    }
  }
  function readCart(){
    return fetch('/cart.js', {headers:{Accept:'application/json'}}).then(function(r){ return r.json(); }).then(absorb).catch(function(){});
  }

  /* The theme's cart. Horizon listens for CartLinesUpdateEvent (@shopify/events, in the theme's
   * import map): the cart icon repaints its count and the drawer morphs in the sections we ask
   * /cart/*.js to render. Loaded only if the theme has those components. */
  var eventsReady = (document.querySelector('cart-items-component, cart-drawer-component, cart-icon') && typeof window.fetch === 'function')
    ? import('@shopify/events').then(function(m){ themeEvents = m; return m; }).catch(function(){ return null; })
    : Promise.resolve(null);
  // function sectionIds(){
  //   return [].map.call(document.querySelectorAll('cart-items-component[data-section-id]'), function(el){ return el.dataset.sectionId; })
  //     .filter(function(id, i, all){ return id && all.indexOf(id) === i; });
  // }
  function sectionIds(){
    // Find section IDs for standard cart components or their parent section wrappers
    var ids = [].map.call(document.querySelectorAll('cart-items-component[data-section-id], cart-drawer-component, cart-drawer, .cart-drawer'), function(el){
      var section = el.closest('[id^="shopify-section-"]');
      return el.dataset.sectionId || (section ? section.id.replace('shopify-section-', '') : null);
    }).filter(function(id){ return !!id; });
    
    // Always fallback to standard Shopify drawer and bubble section IDs
    ids.push('cart-drawer', 'cart-icon-bubble');
    
    // Return unique values
    return ids.filter(function(id, i, all){ return id && all.indexOf(id) === i; });
  }
  function notifyTheme(el, action, line, cart){
    var E = themeEvents && themeEvents.CartLinesUpdateEvent;
    if (!E) return;
    try {
      var d = E.createPromise();
      selfEvent = true;
      el.dispatchEvent(new E({action: action, context: 'product', lines: [line], promise: d.promise}));
      selfEvent = false;
      d.resolve({cart: E.createCartFromAjaxResponse(cart), detail: {items: cart.items, sections: cart.sections, itemCount: cart.item_count, source: 'ebf-sale', didError: false}});
    } catch (e) { selfEvent = false; }
  }
  eventsReady.then(function(m){
    if (!m || !m.StandardEvents) return;
    // A line changed in the drawer (or anywhere else): re-read, so the pills stay true.
    document.addEventListener(m.StandardEvents.cartLinesUpdate, function(e){
      if (selfEvent) return;
      (e.promise || Promise.resolve()).then(readCart, readCart);
    });
  });
  /* Open the drawer: a dev hook first, then Horizon's <theme-drawer id="cart-drawer">, then the
   * old pop-cart drawer. If none exists the add still happened and the bar says so. */
  function openDrawer(){
    if (typeof window.EBF_OPEN_CART === 'function') { try { window.EBF_OPEN_CART(); } catch(e){} return; }
    var cdc = document.querySelector('cart-drawer-component');
    var drawer = document.getElementById('cart-drawer') || (cdc && cdc.closest('theme-drawer'));
    if (drawer && typeof drawer.open === 'function') { if (!drawer.isOpen) drawer.open(); return; }
    var form = document.querySelector('.pop-cart form'), trigger = document.querySelector('.js-cart-drawer-trigger');
    if (!trigger) return;
    var swap = form ? fetch('/?sections=pop-cart', {headers:{Accept:'application/json'}}).then(function(r){ return r.json(); }).then(function(res){
      var box = document.createElement('div'); box.innerHTML = res['pop-cart'] || '';
      var fresh = box.querySelector('form'); if (fresh) form.innerHTML = fresh.innerHTML;
    }).catch(function(){}) : Promise.resolve();
    swap.then(function(){ trigger.click(); });
  }
  function refreshDrawerOnly(){
    // 1. Dispatch generic events that many custom themes listen to
    document.dispatchEvent(new CustomEvent('cart:updated'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // 2. Fallback to manually fetching and replacing the cart HTML (just like openDrawer does)
    var form = document.querySelector('.pop-cart form');
    if (!form) return;
    
    fetch('/?sections=pop-cart', {headers:{Accept:'application/json'}})
      .then(function(r){ return r.json(); })
      .then(function(res){
        var box = document.createElement('div'); box.innerHTML = res['pop-cart'] || '';
        var fresh = box.querySelector('form'); 
        if (fresh) form.innerHTML = fresh.innerHTML;
      }).catch(function(){});
  }
  function paintPill(a){
    if (a.dataset.pre === '1') { a.classList.remove('ebf-is-added'); a.removeAttribute('aria-pressed'); a.removeAttribute('role'); return; }
    var id = idOf(a);
    if (!id || a.getAttribute('aria-disabled') === 'true') { a.classList.remove('ebf-is-added'); a.removeAttribute('aria-label'); return; }
    var on = !!snap.lines[id], what = nameOf(a);
    a.classList.toggle('ebf-is-added', on);
    a.setAttribute('role', 'button'); a.setAttribute('aria-pressed', on ? 'true' : 'false');
    a.setAttribute('aria-label', on ? what + ' is in your cart. Remove it' : 'Add ' + what + ' to cart');
  }
  function render(){
    allPills().forEach(paintPill);
    var B = bar(), sticky = B && B.querySelector('.ebf-sticky');
    var sCta = B && B.querySelector('#ebf-stickyCta'), sCart = B && B.querySelector('#ebf-stickyCart');
    var mode = (B && B.dataset.cartMode) || 'checkout';
    var n = mode === 'off' ? 0 : snap.count;
    cartN = n;
    if (!sticky || !sCta || !sCart) { paint(); return; }
    if (n) {
      sCart.innerHTML = '<b>' + noun(n) + '</b> ' + sCart.dataset.line + ' · <span class="ebf-fig">' + cash(snap.total) + '</span>';
      sCart.hidden = false;
      sCta.textContent = sCta.dataset.checkout; sCta.href = mode === 'cart' ? '/cart' : '/checkout'; sCta.dataset.basket = '1';
      sCta.setAttribute('aria-label', sCta.dataset.checkout + ', ' + noun(n) + ' ' + sCart.dataset.line);
      sticky.classList.add('ebf-is-cart');
    } else {
      sCart.hidden = true; sCart.textContent = '';
      sCta.textContent = sCta.dataset.idle; sCta.href = sCta.dataset.idleHref; delete sCta.dataset.basket;
      sCta.removeAttribute('aria-label');
      sticky.classList.remove('ebf-is-cart');
    }
    paint();
  }
  function announce(msg){
    var B = bar(), live = B && B.querySelector('#ebf-cart-live');
    if (!live) return; live.textContent = ''; setTimeout(function(){ live.textContent = msg; }, 50);
  }
  function toggle(a){
    var id = idOf(a); if (!id || a.dataset.busy) return;
    var name = nameOf(a), price = parseFloat(a.dataset.price) || 0, had = snap.lines[id];
    a.dataset.busy = '1';
    var label = a.querySelector('.ebf-qa__l--idle') || a.querySelector('.ebf-qa__l'), was = label ? label.textContent : '';
    if (label && !had) label.textContent = 'Adding…';
    eventsReady.then(function(){
      var ids = sectionIds(), extra = ids.length ? {sections: ids.join(','), sections_url: location.pathname} : {};
      return had
        ? api('/cart/change.js', Object.assign({id: had.key, quantity: 0}, extra))
        : api('/cart/add.js', {items: [{id: Number(id), quantity: 1}]}).then(function(){ return api('/cart/update.js', Object.assign({attributes: cartAttrs()}, extra)); });
    }).then(function(c){
        absorb(c);
        notifyTheme(a, had ? 'remove' : 'add', had ? {id: had.key, quantity: 0} : {merchandiseId: String(id), quantity: 1}, c);
        track(had ? 'lp_kit_remove' : 'lp_add_to_cart', {cta: a.dataset.cta || 'quick-add', kit: name.slice(0, 40), variant_id: id, price: price});
        announce(name + (had ? ' removed. ' : ' added to cart. ') + noun(snap.count) + ' in cart.');
        
        // NEW LOGIC: Open on add, just refresh HTML on remove
        if (!had) {
            openDrawer();
        } else {
            refreshDrawerOnly();
        }
      })
      .catch(function(){ if (!had && a.getAttribute('href')) location.href = a.getAttribute('href'); })   // the plain additive link, to /cart
      .then(function(){ delete a.dataset.busy; if (label && label.textContent === 'Adding…') label.textContent = was; });
  }
  function initPills(root){
    root.querySelectorAll('a.ebf-qa').forEach(function(a){
      a.addEventListener('click', function(e){
        if (a.dataset.pre === '1') return;   // a pre-order pill is a plain link to the PDP
        if (a.getAttribute('aria-disabled') === 'true') { e.preventDefault(); return; }
        e.preventDefault(); toggle(a);
      });
      a.addEventListener('keydown', function(e){ if (a.dataset.pre !== '1' && e.key === ' ') { e.preventDefault(); toggle(a); } });
      // The model chips and the size control rewrite the pill's href; repaint for the new variant.
      if ('MutationObserver' in window) new MutationObserver(function(){ paintPill(a); }).observe(a, {attributes: true, attributeFilter: ['href']});
    });
  }
  function initBar(root){
    var sCta = root.querySelector('#ebf-stickyCta');
    if (sCta) sCta.addEventListener('click', function(){ if (sCta.dataset.basket) track('lp_checkout_click', {cta: sCta.dataset.cta || 'sticky', kit: String(snap.count)}); });
  }

  /* ---------- set up ---------- */
  function initRoot(root){
    if (root.dataset.ebfReady) return; root.dataset.ebfReady = '1';
    initPills(root);   // before the pickers, so their first repaint is observed
    initPicks(root);
    initSizes(root);
    initRails(root);
    if (root.classList.contains('ebf-lp--bar')) initBar(root);
    if (root.querySelector('.ebf-hero')) watchHero();
  }
  /* ---------- Cart Drawer Sync ---------- */
  // Detect clicks on the drawer's remove and quantity buttons
  document.addEventListener('click', function(e) {
    if (e.target.closest('.js-remove-cart-item, .js-qty__button')) {
      // The drawer's AJAX takes a moment to process. Poll the cart shortly after the click.
      setTimeout(readCart, 400);
      setTimeout(readCart, 1000);
      setTimeout(readCart, 2000);
    }
  });

  // Detect direct typing into the quantity input (if a user changes 1 to 0)
  document.addEventListener('change', function(e) {
    if (e.target.matches('.js-qty__input')) {
      setTimeout(readCart, 500);
      setTimeout(readCart, 1500);
    }
  });

  // Catch generic cart events that many custom themes broadcast
  document.addEventListener('cart:updated', readCart);
  document.addEventListener('cart:refresh', readCart);
  function start(){
    document.querySelectorAll('.ebf-lp').forEach(initRoot);
    watchHero();
    render(); readCart(); tick();
  }
  // The theme editor re-renders one section at a time.
  document.addEventListener('shopify:section:load', function(e){
    var root = e.target.querySelector('.ebf-lp');
    if (root) { initRoot(root); current = null; applyPhase(); render(); }
  });
  addEventListener('pageshow', function(e){ if (e.persisted) readCart(); });   // back from checkout: re-read, never assume
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
